import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import Sidebar from '../Components/Sidebar';
import DashboardHome from './DashboardHome';
import DashboardCreate from './DashboardCreate';
import DashboardPerformance from './DashboardPerformance';
import NotificationBell from '../Components/NotificationBell';
import ProfileMenu from '../Components/ProfileMenu';
import DashboardHandIn from './DashboardHandIn';
import DashboardSettings from './DashboardSettings';
import DashboardCampaigns from './DashboardCampaigns';

import CreatorProgressModal from './CreatorProgressModal';
import ClientOnboarding from './ClientOnboardingForm';
import { useDashboard } from '../hooks/useDashboard';
import FindCreatorsPanel from './FindCreatorsPanel';
import CreditPurchaseModal from './CreditPurchaseModal';
import AdminBrandSignups from './AdminBrandSignups';
import { getCreditPackages } from '../services/apiService';

const DEFAULT_PROFILE_IMAGE = "https://firebasestorage.googleapis.com/v0/b/ugc3-846c7.firebasestorage.app/o/default-profile.png%2Fopenart-image_xvyM7zue_1747323721635_raw.png?alt=media&token=37894604-c3f7-40f2-84c8-c84cac45f8ea";

function CreditsBox({ credits, onAddCredits }) {
  return (
    <div className="header-credits-box">
      <span className="header-credits-amount">{credits.toFixed(2)}</span>
      <span className="header-credits-label">Credits</span>
      <button 
        className={`header-add-credits-btn ${credits === 0 ? 'pulse-animation' : ''}`} 
        title="Add credits"
        onClick={onAddCredits}
      >
        +
      </button>
    </div>
  );
}

function Dashboard() {
  const [showWelcome, setShowWelcome] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const { userData, campaigns, activeCreators, metrics, loading, error } = useDashboard();
  const [startBriefForCampaign, setStartBriefForCampaign] = useState(null);
  const [autoOpenCreateCampaignModal, setAutoOpenCreateCampaignModal] = useState(false);
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [findCreatorsContext, setFindCreatorsContext] = useState(null);
  const [handInContext, setHandInContext] = useState(null);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [creditPackages, setCreditPackages] = useState(null);
  const [creditPackagesPrefetching, setCreditPackagesPrefetching] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowWelcome(false), 2500);
    return () => clearTimeout(timer);
  }, []);

  // Prefetch credit packages once so the first open of the credits modal is instant
  useEffect(() => {
    let isMounted = true;

    const prefetchCreditPackages = async () => {
      try {
        setCreditPackagesPrefetching(true);
        const packages = await getCreditPackages();
        if (isMounted) {
          setCreditPackages(packages);
        }
      } catch (err) {
        // Non-fatal: the modal will fall back to loading its own data
        if (isMounted) {
          console.error('Error prefetching credit packages:', err);
        }
      } finally {
        if (isMounted) {
          setCreditPackagesPrefetching(false);
        }
      }
    };

    prefetchCreditPackages();

    return () => {
      isMounted = false;
    };
  }, []);

  // Check for payment success in URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'success') {
      // Show success message
      const sessionId = urlParams.get('session_id');
      console.log('Payment successful! Session ID:', sessionId);
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Show success notification
      // Credits will update automatically via real-time listener in useDashboard hook
      // No need to reload - the webhook will update Firestore and the listener will pick it up
    } else if (paymentStatus === 'cancelled') {
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <div className="loading-text">Loading your dashboard...</div>
      </div>
    );
  }

  if (error) {
    return <div className="error">Error loading dashboard: {error.message}</div>;
  }

  const renderContent = () => {
    // Onboarding check removed; handled by RequireOnboarding
    switch (activeTab) {
      case 'home':
        return <DashboardHome clientName={userData?.brandName} setActiveTab={setActiveTabWithModal} campaigns={campaigns} metrics={metrics} credits={userData?.credits ?? 0} />;
      case 'create':
        return <DashboardCreate setActiveTab={setActiveTabWithModal} setStartBriefForCampaign={setStartBriefForCampaign} selectedCampaign={campaigns && campaigns[0] ? campaigns[0].name : ''} />;
      case 'performance':
        return <DashboardPerformance setActiveTab={setActiveTabWithModal} campaigns={campaigns} metrics={metrics} />;
      case 'handin':
        return <DashboardHandIn campaigns={campaigns} openSubmissionId={handInContext?.openSubmissionId} openCampaignId={handInContext?.openCampaignId} openBriefId={handInContext?.openBriefId} />;
      case 'settings':
        return <DashboardSettings userData={userData} />;
      case 'campaigns':
        return <DashboardCampaigns campaigns={campaigns} activeCreators={activeCreators} setActiveTab={setActiveTabWithModal} setStartBriefForCampaign={setStartBriefForCampaign} autoOpenCreateCampaignModal={autoOpenCreateCampaignModal} onModalOpened={() => setAutoOpenCreateCampaignModal(false)} />;
      case 'findCreators':
        return <FindCreatorsPanel onClose={() => setActiveTab('campaigns')} isModal={false} context={findCreatorsContext} />;
      case 'admin':
        return <AdminBrandSignups />;

      default:
        return <DashboardHome clientName={userData?.brandName} setActiveTab={setActiveTabWithModal} campaigns={campaigns} metrics={metrics} credits={userData?.credits ?? 0} />;
    }
  };

  // Custom setActiveTab to support auto-opening the modal and passing context
  const setActiveTabWithModal = (tab, options = {}) => {
    // Clear Hand-In context first if navigating to Hand-In without explicit submission context
    if (tab === 'handin' && !(options.openSubmissionId || options.openCampaignId || options.openBriefId)) {
      setHandInContext(null);
    }
    
    setActiveTab(tab);
    
    if (tab === 'campaigns' && options.autoOpenCreateModal) {
      setAutoOpenCreateCampaignModal(true);
    }
    if (tab === 'findCreators' && options.openCreatorId) {
      setFindCreatorsContext({ openCreatorId: options.openCreatorId });
    } else {
      setFindCreatorsContext(null);
    }
    if (tab === 'handin' && (options.openSubmissionId || options.openCampaignId || options.openBriefId)) {
      // Only set context if explicitly provided (from Review Content button or Ready to Review tag)
      setHandInContext({
        openSubmissionId: options.openSubmissionId,
        openCampaignId: options.openCampaignId,
        openBriefId: options.openBriefId
      });
    } else if (tab !== 'handin') {
      // Clear context when navigating away from Hand-In
      setHandInContext(null);
    }
  };

  const getWelcomeMessage = () => {
    if (!userData) return "Welcome! 👋";
    const brandName = userData.brandName;
    if (!brandName) {
      return "Welcome! 👋";
    }
    return `Welcome, ${brandName}!`;
  };

  const userDataWithDefaultImage = userData ? {
    ...userData,
    profileImage: userData.profileImage || DEFAULT_PROFILE_IMAGE
  } : null;

  return (
    <div className="dashboard-wrapper">
      {showWelcome ? (
        <div className="welcome-screen">
          <h1>{getWelcomeMessage()}</h1>
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          <Sidebar 
            activeTab={activeTab} 
            setActiveTab={(tab) => {
              // When clicking Hand-In from menu, clear context to prevent auto-opening modal
              if (tab === 'handin') {
                setHandInContext(null);
              }
              setActiveTab(tab);
            }}
          />
          <div className="dashboard-content brand-dashboard-content-spacing">
            <div className="dashboard-header-bar">
              <CreditsBox 
                credits={userDataWithDefaultImage?.credits ?? 0} 
                onAddCredits={() => setShowCreditPurchaseModal(true)}
              />
              <div className="dashboard-user-info">
                <div className="header-controls">
                  <NotificationBell 
                    setActiveTab={setActiveTab} 
                    setShowCreatorModal={setShowCreatorModal}
                    setSelectedCreator={setSelectedCreator}
                  />
                  <ProfileMenu userData={userDataWithDefaultImage} setActiveTab={setActiveTab} />
                </div>
              </div>
            </div>
            {renderContent()}
          </div>
        </>
      )}
      
      {/* Creator Progress Modal */}
      {showCreatorModal && selectedCreator && (
        <CreatorProgressModal
          isOpen={showCreatorModal}
          onClose={() => {
            setShowCreatorModal(false);
            setSelectedCreator(null);
          }}
          creatorData={selectedCreator}
        />
      )}

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        isOpen={showCreditPurchaseModal}
        onClose={() => setShowCreditPurchaseModal(false)}
        currentCredits={userDataWithDefaultImage?.credits ?? 0}
        initialPackages={creditPackages}
        onPurchaseSuccess={() => {
          setShowCreditPurchaseModal(false);
          // User data will refresh automatically via useDashboard hook
        }}
      />
    </div>
  );
}

export default Dashboard;
